import { logger } from "@/helpers/tslog";
import { saveCandidatePlaceholders } from "@/utils/candidatePlaceholders";
import { DialogMessageType } from "../types/commands";
import { contentManagerQuickRefresh, enableButton, extractSlideImages, insertChartImage, openContentReadyTaskpane } from "@/commands/commands";
import { setSelectedShape } from "./sharePricePerformance/selectedPlaceHolderLocalStorage";
import { deleteParamsValid } from "@/utils/localstorage-params";
import { resetEditLLM } from "./alphaAgent/resetLLM";
import { openConfirmPlaceholder, openContentFraming, updatePlaceholderPopupOpen } from "./popupDialogManager";
import getAllShapesFromPlaceholderSlide from "@/helpers/slide-wireframing/getAllShapesFromPlaceholderSlide";
import { insertDataToPlaceholder } from "../helpers/slide-wireframing/insertDataToShapes";
import { getAllSlideImages } from "@/utils/slideUtils";
import {
  addActiveSlidesJob,
  notifySlidesContentReady,
  setContentReadyMode,
} from "@/utils/slides/slidesJobs";
import type { OpenContentReadySlidesMessage } from "@/utils/slides/slidesTaskpaneContract";


export async function handleMessage(
  args: Office.DialogParentMessageReceivedEventArgs,
  dialog: Office.Dialog,
  event?: Office.AddinCommands.Event
) {
  logger.info("Message received from popup:", args.message);

  // Try JSON parse for structured messages from Content Manager
  let parsedMsg: any = null;
  try {
    parsedMsg = JSON.parse(args.message);
  } catch {}

  // Prefer type from parsed JSON if present
  const messageType = parsedMsg?.type || args.message;

  switch (messageType) {
    case DialogMessageType.REQUEST_SLIDE_IMAGES:
      try {
        const { slides, selectedSlideIndex, selectedSlideId } = await getAllSlideImages();
        dialog.messageChild(
          JSON.stringify({
            type: "SLIDE_IMAGES",
            slides,
            selectedSlideIndex,
            selectedSlideId,
          })
        );
      } catch (e: unknown) {
        const err = e instanceof Error ? e.message : String(e);
        logger.error("REQUEST_SLIDE_IMAGES failed", e);
        dialog.messageChild(
          JSON.stringify({
            type: "SLIDE_IMAGES_ERROR",
            error: err,
          })
        );
      }
      break;
    case "REFRESH_CHARTS":
      try {
        (window as any).postMessage({ type: "REFRESH_CHARTS" }, "*");
      } catch {}
      break;
    case DialogMessageType.OPEN_PLACEHOLDER:
      deleteParamsValid();
      await openPlaceholderSelectionPopup(dialog);
      break;
    case DialogMessageType.CLOSE_PLACEHOLDER:
      deleteParamsValid();
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
        await placeholderSelection();
      });
      // Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
      //   logger.info("Document selection changed, calling selectionChatImage.");
      //   await selectionChatImage();
      // });
      openPopupAfterPlaceholderSelection(dialog, "/popup.html");
      break;

    case DialogMessageType.CLOSE_PLACEHOLDER_ALPHA_AGENT:
      deleteParamsValid();
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
        await placeholderSelection();
      });
      // Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
      //   logger.info("Document selection changed, calling selectionChatImage.");
      //   await selectionChatImage();
      // });
      openPopupAfterPlaceholderSelection(dialog, "/alpha-popup.html");

      // dialog.close();
      // alphaPopupOpen(event);
      break;
    case DialogMessageType.CLOSE_PLACEHOLDER_ALPHA_AGENT_EDIT:
      deleteParamsValid();
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
        await placeholderSelection();
      });
      Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
        logger.info("Document selection changed, calling selectionChatImage.");
        await selectionChatImage();
      });
      openPopupAfterPlaceholderSelection(dialog, "/alphaEditPopup.html");
      break;
    case DialogMessageType.CLOSE_PLACEHOLDER_PLACEHOLDER_POPUP:
      deleteParamsValid();
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
        await placeholderSelection();
      });
      Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
        logger.info("Document selection changed, calling selectionChatImage.");
        await selectionChatImage();
      });
      openPopupAfterPlaceholderSelection(dialog, "/placeholderPopup.html");
      break;
    case DialogMessageType.OPEN_PLACEHOLDER_ALPHA_CREATE:
      deleteParamsValid();
      dialog.close();
      setTimeout(() => {
        const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
        const popupUrl = `${frontendUrl}/placeholderPopup.html`;

        Office.context.ui.displayDialogAsync(popupUrl, { height: 85, width: 55, displayInIframe: true }, (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            logger.error("Failed to open dialog:", result.error.message);
          } else {
            dialog = result.value;
            dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args: any) => {
              await handleMessage(args, dialog, event);
            });
            var dialog = result.value;
            dialog.addEventHandler(Office.EventType.DialogEventReceived, async function () {
              logger.info("Dialog closed. Reason:");
              await resetEditLLM();
              localStorage.removeItem("messages");
              deleteParamsValid();
            });
          }
        });
      }, 1000);
      break;
    case DialogMessageType.CLOSE_CONTENT_MANAGER_OPEN_ALPHA_EDIT:
      deleteParamsValid();
      dialog.close();
      openPopupAfterPlaceholderSelection(dialog, "/alphaEditPopup.html");
      break;
    case DialogMessageType.CONTENT_MANAGER_REFRESH:
      deleteParamsValid();
      setTimeout(() => { 
        contentManagerQuickRefresh(parsedMsg?.placeholder);
      }, 1000);
      break;
    case DialogMessageType.CLOSE_CONTENT_MANAGER_OPEN_PLACEHOLDER_UPDATE_POPUP:
      deleteParamsValid();
      dialog.close();
      setTimeout(() => {
        updatePlaceholderPopupOpen();
      }, 1000);
      break;
    case DialogMessageType.OPEN_CONFIRM_PLACEHOLDER:
      deleteParamsValid();
      dialog.close();
      setTimeout(() => {
        openConfirmPlaceholder();
      }, 1000);
      break;
    case DialogMessageType.CLOSE_CONFIRM_PLACEHOLDER:
      deleteParamsValid();
      dialog.close();
      setTimeout(async () => {
        const raw = localStorage.getItem("slideShapesData");
        if (!raw) {
          return;
        }
        const parsed = JSON.parse(raw) as any; 
        await getAllShapesFromPlaceholderSlide(parsed.slideId, parsed.targetShapeId);
        openContentFraming();
      }, 1000);
      break;
    case DialogMessageType.INSERT_DATA_TO_PLACEHOLDER:
      deleteParamsValid();
      dialog.close();
      setTimeout(() => {
        insertDataToPlaceholder();
      }, 1000);
      break;
    case DialogMessageType.CLOSE || DialogMessageType.CHAT_CLOSE:
      deleteParamsValid();
      dialog.close();
      break;
    case DialogMessageType.INSERT_CHART:
      await insertChartImage(event);
      break;
    case DialogMessageType.EXTRACT_SLIDES:
      await extractSlideImages(event);
      break;
    case DialogMessageType.OPEN_CONTENT_READY_SLIDES: {
      handleOpenContentReadySlides(parsedMsg as OpenContentReadySlidesMessage | undefined);
      break;
    }
  }
}

export const placeholderSelection = async () => {
  try {
    await PowerPoint.run(async (context) => {
      const selectedShapes = context.presentation.getSelectedShapes();
      selectedShapes.load("items");
      await context.sync();

      if (selectedShapes.items.length === 0) {
        localStorage.setItem("placeholderError", "❌ No shapes selected.");
        logger.error("❌ No shapes selected.");
        return;
      }

      logger.info(selectedShapes.items);
      const shape = selectedShapes.items[0];
      const slide = shape.getParentSlide(); // <-- Correct property
      slide.load(["id"]);
      await context.sync();

      shape.load(["name", "width", "height", "left", "top", "type"]);
      await context.sync();

      const slideNumber = await getSlideNumberById(slide.id); 
      
      let selectionData = {
        shapeId: shape.id,
        slideId: slide.id,
        slideIndex: slide.id,
        slideNumber: slideNumber,
        name: shape.name,
        width: shape.width,
        height: shape.height,
        left: shape.left,
        top: shape.top,
        shapeType: shape.type,
        timestamp: new Date().getTime(),
      };

      setSelectedShape(selectionData);

      localStorage.setItem("placeholderError", "");
    });
  } catch (error) {
    logger.error(error);
  }
};

export const getSlideNumberById = async (targetId: string): Promise<number | null> => {
  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();

    const idx = slides.items.findIndex(s => s.id === targetId);
    return idx >= 0 ? idx + 1 : null; // 1-based like what users see
  });
};

export const openPlaceholderSelectionPopup = async (dialog: Office.Dialog) => {
  if (dialog) {
    dialog.close();
    dialog = null;
  }
  setTimeout(() => {
    const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
    const placeholderUrl = `${frontendUrl}/placeholder.html`;

    Office.context.ui.displayDialogAsync(placeholderUrl, { height: 10, width: 20, displayInIframe: true }, (result) => {
      Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async () => {
        await placeholderSelection();
      });

      if (result.status === Office.AsyncResultStatus.Failed) {
        logger.info("Failed to open dialog:", result.error.message);
      } else {
        dialog = result.value;
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (args: Office.DialogParentMessageReceivedEventArgs) => handleMessage(args, dialog)
        );
        var dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogEventReceived, async function () {
          logger.info("Dialog closed. Reason:");
          deleteParamsValid();
        });
      }
    });
  }, 1000);
};

function openPopupAfterPlaceholderSelection(dialog: Office.Dialog, path = "/popup.html") {
  dialog.close();
  dialog = null;

  setTimeout(() => {
    const frontendUrl = process.env.REACT_APP_PUBLIC_URL;
    const popupUrl = `${frontendUrl}${path}`;

    Office.context.ui.displayDialogAsync(popupUrl, { height: 85, width: 55, displayInIframe: true }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
      } else {
        dialog = result.value;
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (args: Office.DialogParentMessageReceivedEventArgs) => handleMessage(args, dialog)
        );
        var dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogEventReceived, async function () {
          logger.info("Dialog closed. Reason:");
          deleteParamsValid();
        });
      }
    });
  }, 1000);
}

export const selectionChatImage = async () => {
  try {
    await PowerPoint.run(async (context) => {
      const selectedShapes = context.presentation.getSelectedShapes();
      selectedShapes.load("items");
      await context.sync();

      if (selectedShapes.items.length === 0) {
        localStorage.setItem("placeholderError", "❌ No shapes selected.");
        logger.error("❌ No shapes selected.");
        await enableButton(false);
        return;
      }

      logger.info(selectedShapes.items);
      const shape = selectedShapes.items[0];
      const slide = shape.getParentSlide(); // <-- Correct property
      slide.load(["id", "index"]);
      await context.sync();

      shape.load(["name", "width", "height", "left", "top", "type", "tags"]);
      await context.sync();

      logger.info("Shape selected:", shape.name, shape.id);

      // Read AlphaScript tags from the shape
      // try {
        // const tagsCollection = shape.tags;
        // if (tagsCollection) {
          // tagsCollection.load("items"); // Load tags collection
          // await context.sync();

          // const allTags = tagsCollection.items;
          // if (allTags.length > 0) {
            // allTags.forEach((tag) => tag.load("key,value")); // Load tag properties
            // await context.sync();

            // const alphaScriptTags: any = {};
            // for (let i = 0; i < allTags.length; i++) {
              // const tag = allTags[i];
              // const tagKey = (tag as any).key;
              // const tagValue = (tag as any).value;

              // Check for AlphaScript metadata tags (case-sensitive)
              // if (
                // tagKey === "WORKBOOKGUID" ||
                // tagKey === "LINKID" ||
                // tagKey === "CHARTNAME" ||
                // tagKey === "RENDER" ||
                // tagKey === "GENERATED"
              // ) {
                // alphaScriptTags[tagKey] = tagValue;
              // }
            // }

            // Log found AlphaScript tags
            // if (Object.keys(alphaScriptTags).length > 0) {
              // logger.info("AlphaScript tags found on shape:", {
                // shapeId: shape.id,
                // shapeName: shape.name,
                // ...alphaScriptTags,
              // });
            // }

            // Check if refresh button should be enabled
            // The refresh button is enabled when:
            // 1. Shape has AlphaScript tags (WORKBOOKGUID and LINKID)
            // 2. There's data in connectedExcelWorkbooks localStorage that matches the shape's tags
              // const tagWorkbook = alphaScriptTags["WORKBOOKGUID"];
              // const tagLinkId = alphaScriptTags["LINKID"];

              // if (tagWorkbook && tagLinkId) {
                // Check if there's matching data in connectedExcelWorkbooks
                // const connectedRaw = localStorage.getItem("connectedExcelWorkbooks");
                // if (connectedRaw) {
                  // const connectedWorkbooks = JSON.parse(connectedRaw) as Array<{ workbookGuid?: string; linkIds?: string[] }>;
                  // if (Array.isArray(connectedWorkbooks)) {
                    // const matchingWorkbook = connectedWorkbooks.find(
                      // (wb) => wb.workbookGuid === tagWorkbook && Array.isArray(wb.linkIds) && wb.linkIds.includes(tagLinkId)
                    // );

                    // if (matchingWorkbook) {
                      // await setRefreshButtonEnabled(true);
                      // logger.info("Refresh button enabled - matching workbook found", {
                        // shapeId: shape.id,
                        // shapeName: shape.name,
                        // workbookGuid: tagWorkbook,
                        // linkId: tagLinkId,
                      // });
                    // } else {
                      // await setRefreshButtonEnabled(false);
                      // logger.info("Refresh button disabled - no matching workbook found", {
                        // shapeId: shape.id,
                        // shapeName: shape.name,
                        // workbookGuid: tagWorkbook,
                        // linkId: tagLinkId,
                      // });
                    // }
                  // }
                // } 
              // } else {
                // await setRefreshButtonEnabled(false);
                // logger.info("Refresh button disabled - missing required AlphaScript tags", {
                  // shapeId: shape.id,
                  // shapeName: shape.name,
                  // hasWorkbookGuid: !!tagWorkbook,
                  // hasLinkId: !!tagLinkId,
                // });
              // }
          // } else {
            // No tags found, disable refresh button
            // await setRefreshButtonEnabled(false);
          // }
        // } else {
          // No tags collection, disable refresh button
          // await setRefreshButtonEnabled(false);
        // }
      // } catch (error) {
        // logger.warn("Could not read AlphaScript tags from shape:", error);
        // await setRefreshButtonEnabled(false);
      // }

      // Defer storing selection until we confirm edit is enabled

      let presentationUniqueId = Office.context.document.settings.get("ALPHASCRIPT");
      const uniqueName = `${presentationUniqueId}-${shape.name}-${shape.id}`;
      const shapeData = localStorage.getItem(uniqueName);
      if (shapeData) {
        localStorage.setItem("documentId", shapeData);
        await enableButton(true);
        const slideNumber = await getSlideNumberById(slide.id); 
        try {
          const selectionData = {
            shapeId: shape.id,
            slideId: slide.id,
            slideIndex: slide.id,
            slideNumber: slideNumber,
            name: shape.name,
            width: shape.width,
            height: shape.height,
            left: shape.left,
            top: shape.top,
            shapeType: shape.type,
            timestamp: new Date().getTime(),
          };

          localStorage.setItem("AlphaEditSelection", JSON.stringify(selectionData));
          localStorage.setItem("placeholderError", "");
        } catch (error) {
          logger.warn("Failed to capture AlphaEditSelection:", error);
        }
      } else {
        await enableButton(false);
        localStorage.removeItem("AlphaEditSelection");
      }

    });
  } catch (error) {
    logger.error(error);
  }
};

// Detect an existing content title and optional sub-title above the selected content placeholder
// Criteria:
// - Width similarity within ±15% OR strong horizontal overlap
// - Positioned directly above the content placeholder with 15–25 px vertical gap
// - Sub-title sits between title and placeholder, near the title
export const detectTitleAndSubtitleAboveSelection = async () => {
  try {
    return await PowerPoint.run(async (context) => {
      const selectedShapes = context.presentation.getSelectedShapes();
      selectedShapes.load("items");
      await context.sync();

      if (selectedShapes.items.length === 0) {
        localStorage.setItem("placeholderError", "❌ No shapes selected.");
        logger.error("❌ No shapes selected.");
        return { title: null, subTitle: null, closestGap: 0 };
      }

      const contentShape = selectedShapes.items[0];
      const slide = contentShape.getParentSlide();
      slide.load(["id", "index", "shapes"]);
      await context.sync();

      contentShape.load(["id", "name", "width", "height", "left", "top", "type"]);
      await context.sync();

      logger.info("Detecting title/sub-title above selection", {
        contentId: contentShape.id,
        contentName: contentShape.name,
        left: contentShape.left,
        top: contentShape.top,
        width: contentShape.width,
        height: contentShape.height,
      });

      const slideShapes = slide.shapes;
      slideShapes.load("items");
      await context.sync();

      const contentLeft = contentShape.left;
      const contentTop = contentShape.top;
      const contentWidth = contentShape.width;
      const contentRight = contentLeft + contentWidth;

      // Define a horizontal search strip directly above the content placeholder
      // Tunable constants
      const regionPadPx = 20; // extend region horizontally by ±20 px
      const regionHeightPx = 30; // vertical strip height
      const horizOverlapThreshold = 0.6; // fallback threshold
      const maxEdgeCenterTolPx = 40; // for alignment normalization
      // Scoring weights
      const weightVertical = 0.45;
      const weightHorizontal = 0.25;
      const weightOverlap = 0.15;
      const bonusPlaceholder = 0.15;
      const bonusTextLike = 0.05;
      const bonusEmpty = 0.1;

      // - Left/Right: same as content, extended slightly (±regionPadPx)
      // - Top: contentTop - regionHeightPx
      // - Bottom: contentTop
      const regionLeft = contentLeft - regionPadPx;
      const regionRight = contentRight + regionPadPx;
      const regionTop = contentTop - regionHeightPx;
      const regionBottom = contentTop;

      const widthTolerance = 0.15; // ±15%
      const horizontalGapTolerancePx = 20; // allow small horizontal misalignment for edges/center

      // Load geometry for all shapes on the slide
      slideShapes.items.forEach((s) => s.load(["id", "name", "width", "height", "left", "top", "type"]));
      await context.sync();

      logger.info("Scanning slide shapes for title/sub-title candidates", {
        totalShapes: slideShapes.items.length,
      });

      // Helper computations
      const computeHorizontalOverlapFraction = (aLeft: number, aRight: number, bLeft: number, bRight: number) => {
        const overlap = Math.min(aRight, bRight) - Math.max(aLeft, bLeft);
        if (overlap <= 0) return 0;
        const minWidth = Math.max(1, Math.min(aRight - aLeft, bRight - bLeft));
        return overlap / minWidth;
      };

      const isWidthSimilar = (w: number, ref: number) => {
        if (ref <= 0 || w <= 0) return false;
        const absDiff = Math.abs(w - ref);
        const relDiffRef = absDiff / ref;
        const relDiffMax = absDiff / Math.max(w, ref);
        const absPxTolerance = 8; // allow small absolute variance
        return relDiffRef <= widthTolerance || relDiffMax <= widthTolerance || absDiff <= absPxTolerance;
      };

      // Determine candidate text-like shapes above the content
      const analyzed = slideShapes.items
        .filter((s) => s.id !== contentShape.id)
        .map((s) => {
          const left = s.left as number;
          const top = s.top as number;
          const width = s.width as number;
          const height = s.height as number;
          const right = left + width;
          const bottom = top + height;
          const horizOverlap = computeHorizontalOverlapFraction(left, right, contentLeft, contentRight);
          const above = bottom <= contentTop;
          const verticalGap = contentTop - bottom;
          const typeStr = String((s as any).type || "").toLowerCase();
          const looksLikeText = typeStr.includes("text");
          const name = String((s as any).name || "");
          const absDiff = Math.abs(width - contentWidth);
          const computedWidthSimilar = isWidthSimilar(width, contentWidth);
          const centerX = left + width / 2;
          const contentCenterX = contentLeft + contentWidth / 2;
          const leftGapAbs = Math.abs(left - contentLeft);
          const centerGapAbs = Math.abs(centerX - contentCenterX);
          const analyzedItem = {
            shape: s,
            left,
            top,
            width,
            height,
            bottom,
            horizOverlap,
            widthSimilar: computedWidthSimilar,
            above,
            verticalGap,
            looksLikeText,
            typeStr,
            name,
            absDiff,
            leftGapAbs,
            centerGapAbs,
          };
          return analyzedItem;
        });

      logger.info("Analyzed shapes above content (geometry and width metrics)", {
        referenceWidth: contentWidth,
        items: analyzed
          .filter((c) => c.above)
          .map((c) => ({
            id: c.shape.id,
            name: c.name,
            left: c.left,
            top: c.top,
            width: c.width,
            height: c.height,
            bottom: c.bottom,
            gapToContentTop: c.verticalGap,
            widthSimilar: c.widthSimilar,
            absDiff: c.absDiff,
            horizOverlap: c.horizOverlap,
            type: c.typeStr,
            leftGapAbs: c.leftGapAbs,
            centerGapAbs: c.centerGapAbs,
          })),
      });

      // First pass: strictly width-similar shapes above the content with left edge aligned within tolerance
      let candidates = analyzed.filter((c) => c.above && c.widthSimilar && c.leftGapAbs <= horizontalGapTolerancePx);

      logger.info("Width-similar candidates (preferred: left-edge aligned)", {
        candidateCount: candidates.length,
        referenceWidth: contentWidth,
        tolerancePct: widthTolerance,
        items: candidates.map((c) => ({
          id: c.shape.id,
          name: c.name,
          width: c.width,
          absDiff: c.absDiff,
          gap: c.verticalGap,
          type: c.typeStr,
          leftGapAbs: c.leftGapAbs,
          leftGapTolerancePx: horizontalGapTolerancePx,
        })),
      });

      // Fallback: allow strong horizontal overlap if no width-similar candidates
      if (candidates.length === 0) {
        candidates = analyzed.filter((c) => c.above && c.horizOverlap >= horizOverlapThreshold);
        logger.info("Fallback to overlap-based candidates", {
          candidateCount: candidates.length,
          items: candidates.map((c) => ({ id: c.shape.id, name: c.name, overlap: c.horizOverlap, gap: c.verticalGap })),
        });
      }

      logger.info("Candidate text-like shapes above content", {
        candidateCount: candidates.length,
      });

      // Title candidates: any candidate whose bottom edge lies within the search region strip
      const titleCandidates = candidates.filter((c) => c.bottom >= regionTop && c.bottom <= regionBottom);

      logger.info("Title candidate gaps", {
        items: titleCandidates.map((c) => ({
          id: c.shape.id,
          name: c.name,
          gap: c.verticalGap,
          bottom: c.bottom,
        })),
      });

      // Attempt to read text content for title candidates to detect empty/boilerplate
      try {
        titleCandidates.forEach((c) => {
          const shapeAny = c.shape as any;
          if (shapeAny && shapeAny.textFrame && shapeAny.textFrame.textRange) {
            shapeAny.textFrame.textRange.load("text");
          }
        });
        await context.sync();
      } catch (e) {
        // If text loading is not supported, ignore
      }

      // Helpers
      const safeLoadShapeText = async (items: any[]) => {
        try {
          items.forEach((c) => {
            const shapeAny = c.shape as any;
            if (shapeAny && shapeAny.textFrame && shapeAny.textFrame.textRange) {
              shapeAny.textFrame.textRange.load("text");
            }
          });
          await context.sync();
        } catch (_) {
          // ignore when not supported
        }
      };

      const computeHorizontalScore = (absDiff: number, leftGapAbs: number, centerGapAbs: number) => {
        const widthPenalty = Math.min(1, (absDiff || 0) / Math.max(1, contentWidth));
        const edgePenalty = Math.min(1, (leftGapAbs || 0) / maxEdgeCenterTolPx);
        const centerPenalty = Math.min(1, (centerGapAbs || 0) / maxEdgeCenterTolPx);
        return Math.max(0, 1 - (0.5 * widthPenalty + 0.25 * edgePenalty + 0.25 * centerPenalty));
      };

      // Compute candidate scores
      type Scored = (typeof analyzed)[0] & { score: number; text?: string };
      await safeLoadShapeText(titleCandidates);

      const scored: Scored[] = titleCandidates.map((c) => {
        let textContent = "";
        try {
          const shapeAny = c.shape as any;
          textContent = String(shapeAny?.textFrame?.textRange?.text || "");
        } catch (_) {}

        // Vertical proximity: smaller gap -> higher score (normalize to 0..1 across 0..regionHeightPx)
        const verticalGapPx = Math.max(0, Math.min(regionHeightPx, c.verticalGap));
        const verticalScore = 1 - verticalGapPx / regionHeightPx;

        // Horizontal alignment: width and edge/center alignment closeness
        const horizontalScore = computeHorizontalScore(c.absDiff, c.leftGapAbs, c.centerGapAbs);

        // Overlap factor to reward strong horizontal covering
        const overlapScore = Math.max(0, Math.min(1, c.horizOverlap || 0));

        // Placeholder vs text box bonus
        const nameLower = (c.name || "").toLowerCase();
        const typeLower = (c.typeStr || "").toLowerCase();
        const isPlaceholder = typeLower.includes("placeholder") || /\b(title|subtitle)\b/.test(nameLower);
        const placeholderBonus = isPlaceholder ? bonusPlaceholder : 0;

        // Text-like soft bonus
        const textLikeBonus = c.looksLikeText ? bonusTextLike : 0;

        // Empty/boilerplate text bonus
        const textLower = textContent.trim().toLowerCase();
        const isEmptyOrBoiler = textLower === "" || textLower.includes("click to add text");
        const emptyBonus = isEmptyOrBoiler ? bonusEmpty : 0;

        // Composite score (weights can be tuned)
        const score =
          weightVertical * verticalScore +
          weightHorizontal * horizontalScore +
          weightOverlap * overlapScore +
          placeholderBonus +
          emptyBonus +
          textLikeBonus;

        return { ...c, score, text: textContent } as Scored;
      });

      // Sort with deterministic tie-breakers
      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if ((b.horizOverlap || 0) !== (a.horizOverlap || 0)) return (b.horizOverlap || 0) - (a.horizOverlap || 0);
        if ((a.verticalGap || 0) !== (b.verticalGap || 0)) return (a.verticalGap || 0) - (b.verticalGap || 0); // closer is better
        if ((a.centerGapAbs || 0) !== (b.centerGapAbs || 0)) return (a.centerGapAbs || 0) - (b.centerGapAbs || 0);
        return String(a.shape.id).localeCompare(String(b.shape.id));
      });

      // Find the closest shape to the placeholder before height filtering
      let closestShape = null;
      let closestGapToPlaceholder = 200;
      if (scored.length > 0) {
        closestShape = scored[0]; // First item after sorting is closest
        closestGapToPlaceholder =  closestShape.verticalGap || 0;
        logger.info("Closest shape to placeholder", {
          id: closestShape.shape.id,
          name: closestShape.shape.name,
          height: closestShape.height,
          gap: closestGapToPlaceholder,
          score: closestShape.score
        });
      }


      // Post-sort filter: exclude oversized candidates (height > 50px)
      const scoredFiltered = scored.filter((c) => Number(c.height) <= 50);

      logger.info("Scored title candidates", {
        items: scoredFiltered.map((c) => ({
          id: c.shape.id,
          name: c.name,
          score: Number(c.score.toFixed(3)),
          gap: c.verticalGap,
          leftGap: c.leftGapAbs,
          centerGap: c.centerGapAbs,
          widthAbsDiff: c.absDiff,
          placeholderHint: (c.typeStr || "").toLowerCase().includes("placeholder"),
          emptyOrBoiler: (c as any).text
            ? (c as any).text.trim() === "" || (c as any).text.toLowerCase().includes("click to add text")
            : false,
          overlap: c.horizOverlap,
        })),
      });

      // Select final title/subTitle per requested rule:
      // - If 2+ shapes: highest score -> subTitle, second highest -> title
      // - If 1 shape: treat as title
      // - If 0: none
      let likelyTitle: any = null;
      let likelySubTitle: any = null;
      if (scoredFiltered.length >= 2) {
        likelySubTitle = scoredFiltered[0];
        likelyTitle = scoredFiltered[1];
      } else if (scoredFiltered.length === 1) {
        likelyTitle = scoredFiltered[0];
      }

      let closestGap = 0;
      
      if(likelyTitle === null && likelySubTitle === null) { // no title and subtitle detected
        closestGap = closestGapToPlaceholder === 200 ? 200 : 60 - closestGapToPlaceholder;
      } else if(likelyTitle !== null && likelySubTitle === null) { // title detected and subtitle not detected
        closestGap = closestGapToPlaceholder === 200 ? 200 : 30 - closestGapToPlaceholder;
      } else { // title and subtitle detected
        closestGap = 200;
      }

      logger.info("Shapes detected", {
        likelyTitle,
        likelySubTitle,
        closestGap,
        closestGapToPlaceholder,
      });
      

      const result = {
        title: likelyTitle
          ? {
              id: likelyTitle.shape.id,
              name: likelyTitle.shape.name,
              left: likelyTitle.left,
              top: likelyTitle.top,
              width: likelyTitle.width,
              height: likelyTitle.height,
              slideId: slide.id,
            }
          : null,
        subTitle: likelySubTitle
          ? {
              id: likelySubTitle.shape.id,
              name: likelySubTitle.shape.name,
              left: likelySubTitle.left,
              top: likelySubTitle.top,
              width: likelySubTitle.width,
              height: likelySubTitle.height,
              slideId: slide.id,
            }
          : null,
        closestGap: closestGap,
      };

      // Persist candidate placeholders for reuse
      saveCandidatePlaceholders(result.title, result.subTitle, closestGap);

      if (result.title) {
        logger.info("Detected existing content title shape", result.title);
      } else {
        logger.info("No existing content title detected above selection");
      }
      if (result.subTitle) {
        logger.info("Detected existing content sub-title shape", result.subTitle);
      } else if (likelyTitle) {
        logger.info("No sub-title detected between title and content");
      }

      return result;
    });
  } catch (error) {
    logger.error(error);
    return { title: null, subTitle: null, closestGap: 0 };
  }
};

function handleOpenContentReadySlides(msg: OpenContentReadySlidesMessage | undefined): void {
  try {
    const jobId = msg?.jobId;
    if (jobId) {
      setContentReadyMode("slides");
      addActiveSlidesJob(jobId, typeof msg?.slideFrameId === "number" ? msg.slideFrameId : undefined);
    }
    notifySlidesContentReady(jobId);
    openContentReadyTaskpane();
  } catch (e) {
    logger.error("OPEN_CONTENT_READY_SLIDES handler failed", e);
  }
}
